import * as React from "react";
import {
  AppBar,
  Box,
  CssBaseline,
  IconButton,
  ThemeProvider,
  Toolbar,
  Typography,
  createTheme,
  Button,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import RedeemRoundedIcon from "@mui/icons-material/RedeemRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import { useEventData } from "../contexts/EventDataContext";
import { getAccessibleTextColor } from "../utils/colors";
import { useLocation } from "react-router-dom";
import { getIcons } from "../utils/icon"; // Import your icon utility function

export default function WishLandingPage() {
  const { eventData, gotoHome, gotoGift, gotoPreviewGift, gotoPreviewDashboard } = useEventData();
  const location = useLocation();
  const isPreview = location.pathname.includes("/preview/");
  const themeData = eventData.theme || {};
  const backgroundColor = themeData.backgroundColor?.value || "#FFF7DC";
  const backgroundImage = themeData.backgroundImage?.value;
  const primaryColor = themeData.mainColor?.value || "#8B4513";
  const secondaryColor = themeData.secondaryColor?.value || "#FFD700";
  const fontColor = themeData.fontColor?.value || "#7A3E00";
  const textInGift = eventData?.gift?.giftButton?.text?.value || "Let's Gift";
  const iconInGift = eventData?.gift?.giftButton?.icon?.value || "heart";

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: primaryColor,
            contrastText: getAccessibleTextColor(primaryColor),
          },
          secondary: {
            main: secondaryColor,
            contrastText: getAccessibleTextColor(secondaryColor),
          },
          background: { default: backgroundColor, paper: backgroundColor },
          text: { primary: fontColor },
        },
        shape: { borderRadius: 16 },
        typography: {
          fontFamily: [
            "Inter",
            "-apple-system",
            "BlinkMacSystemFont",
            "Segoe UI",
            "Roboto",
            "Helvetica",
            "Arial",
            "sans-serif",
          ].join(","),
          button: { textTransform: "none", letterSpacing: 2 },
        },
      }),
    [primaryColor, secondaryColor, backgroundColor, fontColor]
  );

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleGotoGift = () => {
    if (isPreview && gotoPreviewGift) {
      gotoPreviewGift();
    } else {
      gotoGift();
    }
  };

  const handleGotoHome = () => {
    if (isPreview && gotoPreviewDashboard) {
      gotoPreviewDashboard();
    } else {
      gotoHome();
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar
        position="sticky"
        color="primary"
        elevation={0}
        sx={{ bgcolor: primaryColor }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="back"
            onClick={handleGotoHome}
          >
            <ArrowBackIosNewRoundedIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              textAlign: "center",
              letterSpacing: 4,
              mr: 6,
              color: getAccessibleTextColor(primaryColor),
            }}
          >
            Wish
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          minHeight: "calc(100vh - 64px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          px: 2,
          bgcolor: backgroundColor,
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            p: 4,
            borderRadius: 2,
            bgcolor: backgroundImage ? "rgba(255,255,255,0.8)" : "transparent",
            // backdropFilter: backgroundImage ? "blur(10px)" : "none",
          }}
        >
          <Box
            sx={{
              width: isMobile ? 160 : 200,
              height: isMobile ? 160 : 200,
              borderRadius: "50%",
              backgroundColor: secondaryColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 0 ${primaryColor}33`,
            }}
          >
            <AutoAwesomeRoundedIcon
              sx={{
                fontSize: isMobile ? 64 : 88,
                color: getAccessibleTextColor(secondaryColor),
              }}
            />
          </Box>
          <Typography
            sx={{
              color: fontColor,
              textAlign: "center",
              fontSize: isMobile ? 20 : 22,
              letterSpacing: 2,
              lineHeight: 1.6,
              mt: 1,
            }}
          >
            Your wish has
            <br />
            been sent.
          </Typography>
          <Box
            sx={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 16,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              px: 2,
            }}
          >
            <Button
              variant="contained"
              onClick={handleGotoGift}
              startIcon={getIcons(iconInGift)}
              sx={{
                width: "100%",
                py: 2,
                borderRadius: 2,
                backgroundColor: primaryColor,
                color: getAccessibleTextColor(primaryColor),
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: primaryColor,
                  opacity: 0.9,
                  boxShadow: "none",
                },
                letterSpacing: 2,
              }}
            >
              {textInGift}
            </Button>
            <Button
              variant="contained"
              onClick={handleGotoHome}
              startIcon={<HomeRoundedIcon />}
              sx={{
                width: "100%",
                py: 2,
                borderRadius: 2,
                backgroundColor: secondaryColor,
                color: getAccessibleTextColor(secondaryColor),
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: secondaryColor,
                  opacity: 0.9,
                  boxShadow: "none",
                },
                letterSpacing: 2,
              }}
            >
              Home Page
            </Button>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
